import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy,
         ViewChild
       } from '@angular/core';
import { SVG, Svg, Image, G, Tspan, Element as SvgElement
       } from '@svgdotjs/svg.js';
import { ColorPaletteService
       } from 'app/controls/color-chooser/color-palette.service';
import { QualityRating
       } from 'app/controls/image-quality-rating/quality-rating';
import { injectMultiversionImageBehavior
       } from 'app/controls/multiversion-image/multiversion-image';
import { ProgressType
       } from 'app/controls/progress-overlay/progress-overlay.component';
import { GalleryImage, GalleryService } from 'app/gallery/gallery.service';
import { ProductDesignerService } from 'app/product-designer.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { ConditionMatcher } from 'collage-designer/condition-matcher';
import { CopyRules } from 'collage-designer/copy-rules';
import { FontInjectorService } from 'collage-designer/font-injector.service';
import { ConfigService } from 'core/config/config.service';
import { GlobalConstants, StageConstants } from 'core/constants';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { StageUtils } from 'core/utils.stage';
import { Subscription } from 'rxjs';

import { ProgressService } from '../../progress/progress.service';
import { CopyRuleConfig, OverlaysLayerEntryConfig, PartInfo, TemplateConfig
       } from '../collage-designer-config';
import { DesignStepService } from '../design-step/design-step.service';
import { ImageLoadingError, ImagePlaceholder
       } from './placeholder/image-placeholder';
import { ActionBarDirective
       } from './placeholder/overlays/action-bar/action-bar.directive';
import { FocusEvent, Placeholder, PlaceholderType
       } from './placeholder/placeholder';
import { PlaceholderService } from './placeholder/placeholder.service';
import { TextPlaceholder } from './placeholder/text-placeholder';
import { StageConfig } from './stage-config';
import { StageFeedback, StageService } from './stage.service';

interface OverlayWrapperSize
{
  width: number;
  height: number;
}

interface LayersInfo {
  overlays: G;
  designOnly: G;
}

@Component({
    selector: 'stage',
    templateUrl: './stage.component.html',
    styleUrls: ['./stage.component.scss'],
    standalone: false
})
export class StageComponent implements AfterViewInit, OnDestroy
{
  // 5 part of 12 of stage width/height  <- must be < 1!
  private static readonly FLEXIBLE_PLACEHOLDER_SIZE: number = 5 / 12;
  private static readonly MAX_OVERLAY_LOADING_ATTEMPTS: number = 3;
  private static readonly STAGE_CREATION_WAIT_TIMEOUT_MS: number = 3000;

  @ViewChild('svgContainer') public svgContainer: any;
  @ViewChild('stageImageSelect') public stageImageSelect: ElementRef;
  @ViewChild('qualityRating') public qualityRating: ElementRef;
  @ViewChild(ActionBarDirective) public actionBars: ActionBarDirective;

  public lowestImageQualityRating: number;
  public displayQualityIndicators: boolean = false;

  private config: StageConfig;
  private stage: Svg;
  private partInfos: Array<PartInfo> = [];
  private mouseDownTarget: EventTarget | undefined = undefined;

  private newImageAvailableSubscription:
      AsyncEventEmitterSubscriber<GalleryImage>;
  private subscriptions: Subscription = new Subscription();
  private stageSubscriptions: Subscription = new Subscription();
  private templateChangedSubscription:
      AsyncEventEmitterSubscriber<TemplateConfig>;
  private stageConfigChangedSubscription:
      AsyncEventEmitterSubscriber<StageConfig>;

  private imagePlaceholders: Array<ImagePlaceholder> = [];
  private textPlaceholders: Array<TextPlaceholder> = [];
  private focusedPlaceholder: Placeholder | undefined = undefined;
  private galleryOpenedByFocus: boolean = false;
  private stageIsInCreation: boolean = false;
  private conditionMatcher: ConditionMatcher;

  private scheduleStageConfigUpdate: () => void = Utils.debounce(
    () => this.stageService.updateStageConfig(this.getCurrentStageConfig()),
    250);

  constructor(
      @Inject(ErrorService) private errorService: ErrorService,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(StageService) private stageService: StageService,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(ToolboxService) private toolboxService: ToolboxService,
      @Inject(ColorPaletteService) private colorPaletteService:
          ColorPaletteService,
      @Inject(PlaceholderService) private placeholderService:
          PlaceholderService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService,
      @Inject(ProgressService) private progressService: ProgressService,
      @Inject(TranslationService) private translationService:
          TranslationService,
      @Inject(ProductDesignerService) private productDesignerService:
          ProductDesignerService,
      @Inject(FontInjectorService) private fontInjectorService,
      @Inject(LoggingService) private loggingService: LoggingService,
      @Inject(ConfigService) configService: ConfigService,
      @Inject(ImageStoreService) private imageStoreService: ImageStoreService,
  )
  {
    this.config = undefined;

    this.conditionMatcher = new ConditionMatcher(
        configService.getProductDesignerComponentConfig().catalogStoreId,
        this.designStepService);
    this.stageConfigChangedSubscription =
        this.stageService.stageConfigChanged.subscribe(async value => {
            this.stageService.isComponentTemplateSwitchRunning = true;
            try {
              await this.applyNewStageConfig(value);
            }
            finally {
              this.stageService.isComponentTemplateSwitchRunning = false;
            }
        });

    this.templateChangedSubscription =
        this.designStepService.templateChanged.subscribe(
          this.finalizePlaceholders.bind(this));

    this.subscriptions.add(
        this.stageService.feedbackRequested.subscribe(
          this.feedbackRequested.bind(this)));

    this.subscriptions.add(
        this.designStepService.createNewPlaceholderInitialized.subscribe(
            type => this.addNewPlaceholder(type)));
  }

  public ngAfterViewInit(): void
  {
    this.svgContainer.nativeElement.getRootNode().addEventListener(
        'click', this.hidePlaceholderMarkersIfNecessary.bind(this));
  }

  private installChangeHook(): void
  {
    this.progressService.addChangeHook({
      action: async () => {
        await this.waitForActiveGalleryImageLoads();
        await this.stageService.updateStageConfig(this.getCurrentStageConfig());
      }
    });

    this.progressService.addChangeHook({
      id: "upload-stage",
      action: async () => {
        const deadline =
            Date.now() + StageComponent.STAGE_CREATION_WAIT_TIMEOUT_MS;
        while (this.stageIsInCreation) {
          if (Date.now() > deadline) {
            return;
          }
          await Utils.sleep(10);
        }

        const designState = this.config.designState;
        const structure = (new XMLSerializer())
            .serializeToString(this.stage.node);

        const info = await this.imageStoreService.postSvg(
          StageUtils.sanitizeStructure(structure));

        this.designStepService.setDesignedStructureUrl(
            info.location, designState);
      }
    });
  }

  private async waitForActiveGalleryImageLoads(): Promise<void>
  {
    while (this.imagePlaceholders.some(p => p.isPending())) {
      await Utils.sleep(200);
    }
  }

  private feedbackRequested(feedback: StageFeedback): void
  {
    switch (feedback) {
      case StageFeedback.HIGHLIGHT_UNCHANGED_PLACEHOLDERS:
        return this.highlightUnchangedPlaceholders();
      default:
        break;
    }
  }

  private highlightUnchangedPlaceholders(): void
  {
    for (const placeholderListItem of
        this.placeholderService.getUnchangedPlaceholders()) {
      placeholderListItem.placeholder.highlight();
    }
  }

  private async addNewPlaceholder(type: PlaceholderType): Promise<void>
  {
    // TODO: At the moment flexible placeholders should only be added to
    //       one part products!
    const doc = this.partInfos[0].doc;

    let element;
    const stageSize = doc.viewbox();
    const stageBorderPaddingFactor =
        (1 - StageComponent.FLEXIBLE_PLACEHOLDER_SIZE) / 2;

    const group = doc.group();
    const wrapper = group.nested();
    wrapper.x(stageBorderPaddingFactor * stageSize.width);
    wrapper.y(stageBorderPaddingFactor * stageSize.height);
    wrapper.width(
        StageComponent.FLEXIBLE_PLACEHOLDER_SIZE * stageSize.width);
    wrapper.height(
        StageComponent.FLEXIBLE_PLACEHOLDER_SIZE * stageSize.height);
    wrapper.attr(StageConstants.ATTRIBUTE_CONTENT_CHANGEABLE, 'true');
    wrapper.attr(
        StageConstants.ATTRIBUTE_ZOOM_MODE, StageConstants.ZOOM_MODE_FIT);
    wrapper.css('overflow', 'hidden');

    this.placeholderService.blurAllPlaceholders(true);

    if (type === PlaceholderType.Text) {
      wrapper.rect().width('100%').height('100%');
      element = wrapper.text(
          this.translationService.get('i18n.template.insertText'));
      element.attr(StageConstants.ATTRIBUTE_TEXT_ANCHOR, 'middle');
      const tspan = element.children().at(0);
      const longside = stageSize.width > stageSize.height ?
          stageSize.width : stageSize.height;
      tspan.css(
          'font-size',
          (longside / StageComponent.FLEXIBLE_PLACEHOLDER_SIZE) + 'px');
      // It should use our default dy not something calculated by svg.js!
      tspan.attr('dy', null); // eslint-disable-line
      const placeholder =
          await this.placeholderService.createTextPlaceholder(element, true);
      await placeholder.setTextValidated(
          (await placeholder.getTextModel()).text);
      const textModel = await placeholder.getTextModel();
      textModel.fontSize *= 0.9;
      await placeholder.setTextModel(textModel);
      this.textPlaceholders.push(placeholder);
      this.prepareTextPlaceholder(placeholder);
    }

    element.attr(StageConstants.ATTRIBUTE_IS_UNCHANGED, 'true');
    element.attr(StageConstants.ATTRIBUTE_IS_VOLATILE_IF_UNCHANGED, 'true');

    StageUtils.ensureCorrectLayerPositions(doc);
  }

  private async applyNewStageConfig(newStageConfig: StageConfig): Promise<any>
  {
    if (newStageConfig.svgStructure !== undefined) {
      this.productDesignerService.suspendDesignerTrigger.emit({suspend: true});
      try {
        await this.stageService.updateStageConfig(newStageConfig);
        await this.setStageConfig(newStageConfig);

        this.updateQualityRating();
      }
      finally {
        this.productDesignerService.suspendDesignerTrigger
          .emit({suspend: false, progressType: ProgressType.Spinner});
      }
    }
  }

  private async autofillItems(
      galleryImages: Array<GalleryImage>): Promise<void>
  {
    if (this.preconditionsFailed() || galleryImages.length < 1) {
      return;
    }

    this.toolboxService.resetPlaceholder();
    const placeholders = this.imagePlaceholders
      .filter((placeholder) =>
          placeholder.type === PlaceholderType.Image &&
          placeholder.isContentChangeable()
      );

    const getPendingImages = () =>
      placeholders.filter(placeholder => placeholder.isPending());

    if (getPendingImages().length < 1 &&
        this.focusedPlaceholder &&
        this.focusedPlaceholder.isUnchanged())
    {
      this.markFocusedPlaceholderPending(galleryImages[0]);
    }

    const freeImages = placeholders
        .filter((placeholder) => placeholder.isUnchanged())
        .filter((placeholder) => !placeholder.isPending());

    const pendingImages = getPendingImages();

    const pendingIds = pendingImages
      .map(placeholder => placeholder.get(StageConstants.ATTRIBUTE_PENDING));

    const freeGalleryImages = galleryImages
      .filter(gi => pendingIds.indexOf(gi.id) === -1);

    const pendingGalleryImages = galleryImages
      .filter(gi => pendingIds.indexOf(gi.id) !== -1);

    await this.autofillFreeItems(freeImages, freeGalleryImages);
    await this.autofillPendingItems(pendingImages, pendingGalleryImages);
  }

  private markFocusedPlaceholderPending(img: GalleryImage): void
  {
    if (! (this.focusedPlaceholder instanceof ImagePlaceholder)) {
      return;
    }

    this.focusedPlaceholder.set(StageConstants.ATTRIBUTE_PENDING, img.id);
  }

  private async autofillPendingItems(
    placeholders: Array<ImagePlaceholder>,
    galleryImages: Array<GalleryImage>): Promise<void>
  {
    await Promise.all(placeholders.map(async placeholder => {
      const id = placeholder.get(StageConstants.ATTRIBUTE_PENDING);
      const matches = galleryImages.filter(gi => gi.id === id);

      if (matches.length > 0) {
        const match = matches.shift();
        placeholder.set(StageConstants.ATTRIBUTE_PENDING, undefined);
        await placeholder.loadGalleryImage(match.id);
      }
    }));
  }

  private async autofillFreeItems(
    placeholders: Array<ImagePlaceholder>,
    galleryImages: Array<GalleryImage>): Promise<void>
  {
    const imgs = placeholders.map(p => p.element);
    const matchings: Array<[GalleryImage, string]> = [];
    for (const galleryImage of galleryImages) {
      const selectedId = selectScoredPlaceholder(imgs, galleryImage);
      if (!selectedId) {
        break;
      }
      imgs.splice(
        imgs.indexOf(Utils.find(imgs, el => el.attr('id') === selectedId)), 1);

      matchings.push([galleryImage, selectedId]);
    }

    await Promise.all(matchings.map(async (matching) => {
      const galleryImage = matching[0];
      const selectedId = matching[1];

      const selectedPlaceholder =
        Utils.find(placeholders, el => el.element.attr('id') === selectedId);

      await selectedPlaceholder.loadGalleryImage(galleryImage.id);
    }));
  }

  private async registerUsedImages(): Promise<void>
  {
    this.galleryService.setImagesAsUnused();

    const imagePlaceholders = this.imagePlaceholders.filter((placeholder =>
        placeholder.type === PlaceholderType.Image));

    await Promise.all(
      imagePlaceholders.map(
          async p => await p.restoreGalleryImageIfNecessary()));
  }

  private preconditionsFailed(): boolean
  {
    return this.config === undefined || this.stage === undefined ||
        this.config.svgStructure === undefined;
  }

  private async setStageConfig(stageConfig: StageConfig): Promise<void>
  {
    this.stageService.setIsStageLoaded(false);
    this.config = stageConfig;
    this.installChangeHook();

    await this.recreateStage();

    if (this.newImageAvailableSubscription === undefined) {
      // neue Bilder machen nur Sinn, wenn wir eine StageConfig haben.
      // deswegen wird erst ab hier auf das Event gelauscht.

      this.newImageAvailableSubscription =
          this.galleryService.newImageAvailable.subscribe(
              async (galleryImage: GalleryImage) => {
                await this.autofillItems([galleryImage]);
              });
    }
    this.stageService.setIsStageLoaded(true);
    await this.stageService.stageConfigApplied.emit(true);
  }

  private async restorePlaceholderContent(): Promise<void>
  {
    const allPlaceholders = []
      .concat(this.imagePlaceholders)
      .concat(this.textPlaceholders);

    await Promise.all(allPlaceholders.map(async placeholder => {
      await placeholder.restoreContent();
    }));
  }

  private async savePlaceholderContentIfNotUnchanged(): Promise<void>
  {
    const allPlaceholders = []
      .concat(this.imagePlaceholders)
      .concat(this.textPlaceholders);

    for (const placeholder of allPlaceholders) {
      await placeholder.saveContentIfChanged();
    }
  }

  private async recreateStage(): Promise<void>
  {
    this.stageIsInCreation = true;
    await this.waitForActiveGalleryImageLoads();
    this.clearStage();
    const viewbox = this.stage.viewbox();
    this.stage.width('100%').height('100%').viewbox(viewbox);
    this.stage.attr('text-rendering', 'geometricPrecision');
    this.stageService.setCurrentStageReference(this.stage);

    this.hideStage();

    this.resetCopiedElements();
    this.applyCopyRulesFromConfig();
    this.readjustCopyFromAttributes();

    const structureUrl = await this.designStepService.getSVGStructureUrl();

    if (!this.stage.attr(StageConstants.ATTRIBUTE_INITIAL_STRUCTURE_URL)) {
      this.stage.attr(
          StageConstants.ATTRIBUTE_INITIAL_STRUCTURE_URL, structureUrl);
    }

    const initialStructureUrl = this.stage.attr(
        StageConstants.ATTRIBUTE_INITIAL_STRUCTURE_URL);

    this.config.initialSvgStructureURL = initialStructureUrl;

    if (!this.designStepService.getInitialSVGStructureUrl()) {
      this.designStepService.setInitialSVGStructureUrl(
          initialStructureUrl ? initialStructureUrl : structureUrl);
    }

    this.colorPaletteService.reset();
    await this.placeholderService.refreshPlaceholders(
        this.stage, this.actionBars);
    this.prepareImages();
    this.prepareTexts();
    await this.registerUsedImages();

    for (const info of this.partInfos) {
      await this.drawOverlays(info);
    }

    this.centerOverlays(this.stage);
    this.rotateIfNecessary(this.stage);
    this.drawStageShroud(this.stage);
    await this.loadMissingFonts(this.stage);

    await this.restorePlaceholderContent();
    await this.savePlaceholderContentIfNotUnchanged();
    await this.autofillItems(this.galleryService.getImages(true));
    this.stageIsInCreation = false;
    this.showStage();
  }

  public hasEmptyImagePlaceholders(): boolean
  {
    return !this.imagePlaceholders.some(
        p => p.isContentChangeable() && p.isUnchanged());
  }

  private resetCopiedElements(): void
  {
    const copied = (<any>this.stage.find('svg'))
        .filter((el: SvgElement) =>
                !!el.attr(StageConstants.ATTRIBUTE_COPIED_FROM));

    for (const el of copied) {
      const copiedPart = el.attr(StageConstants.ATTRIBUTE_COPIED_FROM);
      el.clear();
      el.image().attr(
          StageConstants.ATTRIBUTE_COPY_FROM, copiedPart);
    }
  }

  private applyCopyRulesFromConfig(): void
  {
    interface BoundRule {
      rule: CopyRuleConfig;
      element: SvgElement;
    }

    const findElementById = (id: string | undefined) =>
      this.stage.find(`#${id}`).at(0);

    const applicableRules = this.designStepService.getPart().copyRules;

    const rulesWithElements = applicableRules
      .map((r: CopyRuleConfig) => <BoundRule> {
        rule: r,
        element: findElementById(r.to.item)
      })
      .filter((it: BoundRule) => !!it.element);

    for (const it of rulesWithElements) {
      it.element.attr(
        StageConstants.ATTRIBUTE_COPY_FROM, it.rule.from.shortForm);
    }
  }

  private readjustCopyFromAttributes(): void
  {
    (<any> this.stage.find('image'))
      .filter((el) => el.attr(StageConstants.ATTRIBUTE_COPY_FROM))
      .forEach((el) => {
        const currentPartId = this.designStepService.getPartId();
        const copyRule =
            CopyRules.decodePlace(el.attr(StageConstants.ATTRIBUTE_COPY_FROM),
            currentPartId);
        if (copyRule.part === currentPartId) {
          const copyFrom = this.stage.find(`#${copyRule.item}`).at(0);
          const copyFromCopyTo =
              copyFrom.attr(StageConstants.ATTRIBUTE_COPY_FROM);
          if (copyFromCopyTo) {
            el.attr(StageConstants.ATTRIBUTE_COPY_FROM, copyFromCopyTo);
          }
        }
      });
  }

  private setStageOpacity(opacity: number): void
  {
    this.svgContainer.nativeElement.style.opacity = opacity;
  }

  private showStage(): void
  {
    this.setStageOpacity(1);
  }

  private hideStage(): void
  {
    this.setStageOpacity(0);
  }

  private clearStage(): void
  {
    if (this.stage !== undefined) {
      // TODO Horrible workaround until svg.js is fixed:
      //      https://github.com/svgdotjs/svg.js/issues/759
      //      Instead of removing the stage from the dom, it is moved to a
      //      temporary node that will be garbage collected eventually.
      //      If we don't do this, a rogue load event on some image
      //      in the old stage might call SVG.parent() and hit a detached node.
      //      Upon which it will raise an error.
      (document.createElement('div')).appendChild(this.stage.node);
    }

    this.stageSubscriptions.unsubscribe();
    this.stageSubscriptions = new Subscription();
    this.clearPlaceholders();

    const parser = new DOMParser();
    const svgDoc =
        parser.parseFromString(this.config.svgStructure, 'image/svg+xml');

    const nativeContainer = this.svgContainer.nativeElement;
    const svgNode = svgDoc.documentElement as unknown as SVGSVGElement;

    nativeContainer.appendChild(svgNode);
    this.stage = SVG(svgNode);

    this.updatePartInfos();

    this.forAllParts((part) => {
      part.doc.group().addClass(StageConstants.GROUP_TAG_DESIGN_ONLY);
      part.doc.group().addClass(StageConstants.GROUP_TAG_OVERLAYS);
    });

    // We only want to fire the click event, if the click was also started
    // at the stage

    const getTarget = (e: Event) => this.mouseDownTarget = e.target;

    this.stage.mousedown(getTarget);
    this.stage.touchstart(getTarget);
    this.stage.click((e: Event) => {
      if (this.mouseDownTarget && e.target === this.mouseDownTarget) {
        this.placeholderService.blurAllPlaceholders(true);
      }
      this.mouseDownTarget = undefined;
    });
  }

  private updatePartInfos(): void
  {
    const embedded = Utils.tupleListToDictionary(
      StageUtils.svgSelectAsArray(this.stage, 'svg')
        .map(d => [d.attr(StageConstants.ATTRIBUTE_PART_ID), d])
        .filter(it => it[0] !== undefined));

    const docs = {
      ...embedded,
      [this.designStepService.getPart().id]: this.stage
    };

    this.partInfos = this
      .designStepService
      .getParts()
      .map(p => ({
        config: p,
        doc: docs[p.editingView.reference || p.id],
      }))
      .filter(Utils.isUnique(p => p.doc));
  }

  private forAllParts(fn: (part: PartInfo) => void): void
  {
    for (const part of this.partInfos) {
      fn(part);
    }
  }

  private clearPlaceholders(): void
  {
    this.finalizePlaceholders();
    this.imagePlaceholders = [];
    this.textPlaceholders = [];
  }

  private removePlaceholder(placeholder: Placeholder): void
  {
    this.scheduleStageConfigUpdate();
    if (placeholder.type === PlaceholderType.Image) {
      this.imagePlaceholders =
          this.imagePlaceholders.filter(p => p !== placeholder);
    }
    else {
      this.textPlaceholders =
          this.textPlaceholders.filter(p => p !== placeholder);
    }
  }

  private finalizePlaceholders(): void
  {
    const placeholders = Utils.flattenArray([
      this.imagePlaceholders, this.textPlaceholders
    ]);

    for (const placeholder of placeholders) {
      placeholder.finalize();
    }
  }

  private prepareImages(): void
  {
    StageUtils.querySelectorAsArray(this.stage.node as SVGSVGElement, 'image')
      .filter(e =>
          !(<HTMLElement> e.parentNode)
            .getAttribute(StageConstants.ATTRIBUTE_ZOOM_MODE) &&
          !StageUtils.isAncestorLinked(e))
      .map(e => SVG(e as SVGElement))
      .map(e => injectMultiversionImageBehavior(e))
      .forEach(i => i.load(i.attr('xlink:href')));

    this.imagePlaceholders = this.placeholderService.getImagePlaceholders();
    for (let placeholder of this.imagePlaceholders) {
      this.stageSubscriptions.add(placeholder.changed.subscribe((data) => {
        this.updateQualityRating();
        this.scheduleStageConfigUpdate();
      }));

      this.stageSubscriptions.add(placeholder.destroyed.subscribe(destroyed => {
        if (destroyed) {
          this.removePlaceholder(placeholder);
        }
      }));

      this.stageSubscriptions.add(placeholder.clicked.subscribe(async () => {
        await this.imagePlaceholderClicked(placeholder);
      }));

      this.stageSubscriptions.add(placeholder.finished.subscribe(() => {
        this.toolboxService.commitChanges();
        this.galleryService.setSelectedImageId(undefined);
        placeholder.blur(true);
      }));

      this.stageSubscriptions.add(placeholder.focused.subscribe((focus) => {
        switch (focus) {
          case FocusEvent.FocusEmpty:
            this.selectPlaceholder(placeholder, false);
            break;
          case FocusEvent.FocusFilled:
            this.selectPlaceholder(placeholder, true);
            break;
          case FocusEvent.Blur:
            this.unselectPlaceholder(placeholder);
            break;

          default: break;
        }
      }));

      // Unschön, aber ein Zurücksetzen des Highlighters über den
      // PlaceholderService ist nicht möglich, da es dann zwischen
      // ImagePlaceholder und dem Service zu einer kreislichen Abhängigkeit
      // (circular dependency) kommt.
      this.stageSubscriptions.add(
          placeholder.removeAllPlaceholderHighlighters.subscribe((value) => {
            for (let iph of this.imagePlaceholders) {
              iph.hidePlaceholderHighlighter();
            }
          }));
    }
  }

  private selectPlaceholder(
    placeholder: Placeholder, hasContent: boolean = false): void
  {
    const previouslyFocused = this.focusedPlaceholder;
    this.focusedPlaceholder = placeholder;

    if (previouslyFocused && previouslyFocused !== placeholder) {
      this.toolboxService.resetPlaceholder();
      previouslyFocused.blur();
    }
  }

  private unselectPlaceholder(placeholder: Placeholder): void
  {
    if (this.focusedPlaceholder !== placeholder) {
      return;
    }

    this.focusedPlaceholder = undefined;

    if (this.galleryOpenedByFocus &&
        this.toolboxService.getActiveToolboxName() === 'GalleryToolbox')
    {
      this.toolboxService.commitChanges();
    }

    this.galleryOpenedByFocus = false;
  }

  private prepareTexts(): void
  {
    this.textPlaceholders = this.placeholderService.getTextPlaceholders();

    for (let placeholder of this.textPlaceholders) {
      this.prepareTextPlaceholder(placeholder);
    }
  }

  private prepareTextPlaceholder(placeholder: TextPlaceholder): void
  {
    this.stageSubscriptions.add(placeholder.changed.subscribe((data) => {
      this.scheduleStageConfigUpdate();
    }));

    this.stageSubscriptions.add(placeholder.destroyed.subscribe(destroyed => {
      if (destroyed) {
        this.removePlaceholder(placeholder);
      }
    }));
  }

  private async imagePlaceholderClicked(
      placeholder: ImagePlaceholder): Promise<void>
  {
    if (this.galleryService.getSelectedImageId()) {
      await placeholder.loadGalleryImage(
          this.galleryService.getSelectedImageId());
      this.galleryService.setSelectedImageId(undefined);

      for (let _placeholder of this.imagePlaceholders) {
        _placeholder.hidePlaceholderHighlighter();
      }

      this.placeholderService.enableTextPlaceholders();
    }
    else if (! placeholder.hasEditableContent()) {
      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'init_photo_upload',
        label: 'image_placeholder',
      });
      this.stageImageSelect.nativeElement.onchange = async () => {
        const inputElement = this.stageImageSelect.nativeElement;

        if (inputElement.value !== '') {
          await placeholder.loadLocalImage(inputElement);
          inputElement.value = '';
        }
      };

      // Open upload dialog only if there are no unused images in the gallery
      // or if the user device is no mobile device.
      if (this.galleryService.getImages(true).length < 1 ||
          !(Utils.isSmallDevice() || Utils.isMediumDevice())) {
        this.stageImageSelect.nativeElement.click();
        placeholder.blur(true);
      }
    }

    const shouldOpenGallery =
      !(Utils.isSmallDevice() || Utils.isMediumDevice()) ||
      !placeholder.hasEditableContent();

    if (shouldOpenGallery && !this.toolboxService.hasActiveToolbox()) {
      this.galleryOpenedByFocus = true;
    }

    if (shouldOpenGallery) {
      this.toolboxService.edit(placeholder, 'GalleryToolbox');
    }
  }

  private getLayers(doc: Svg): LayersInfo
  {
    return {
      overlays: doc
        .find(`#${doc.id()} > .${StageConstants.GROUP_TAG_OVERLAYS}`)
        .at(0) as G,

      designOnly: doc
      .find(`#${doc.id()} > .${StageConstants.GROUP_TAG_DESIGN_ONLY}`)
        .at(0) as G,
    };
  }

  private async drawOverlays(info: PartInfo): Promise<void>
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return;
    }

    const config = info.config.editingView.overlays;
    const layers = this.getLayers(info.doc);

    if (config.editableArea) {
      this.applyEditableArea(info);
    }

    const imagesToDraw = [];
    for (const i of config.images) {
      if (await this.conditionMatcher.areConditionFullfilled(i.conditions)) {
        imagesToDraw.push(i);
      }
    }
    const images = await this.drawOverlayImages(
        info, imagesToDraw);

    const inlineSVGsToDraw = [];
    for (const i of config.inlineSVGs) {
      if (await this.conditionMatcher.areConditionFullfilled(i.conditions)) {
        inlineSVGsToDraw.push(i);
      }
    }
    const inlineSVGs = await this.drawOverlayInlineSVGs(
        info, inlineSVGsToDraw);

    for (const image of images) {
      layers.overlays.add(image);
    }
    for (const inlineSVG of inlineSVGs) {
      layers.overlays.add(inlineSVG);
    }
  }

  private applyEditableArea(info: PartInfo): void
  {
    this.saveOriginalViewBox(info.doc);
    info.doc.viewbox(StageUtils.scaleEditableArea(
        info.doc.viewbox(), info.config, this.config.rotation));
  }

  private saveOriginalViewBox(doc: Svg): void
  {
    doc.attr(StageConstants.ATTRIBUTE_ORIGINAL_VIEWBOX, doc.attr('viewBox'));
    const box = doc.viewbox();
    (<any>doc).dimensions = () => box;
  }

  private drawStageShroud(doc: Svg): void
  {
    const box = doc.viewbox();
    const width = box.width;
    const height = box.height;

    const rectPath = coords =>
      'm ' + coords.map(c => c.join(' ')).join(' ') + ' z';

    const outer = rectPath([
      [-width + box.x, -height + box.y],
      [width * 3, 0],
      [0, height * 3],
      [-width * 3, 0],
      [0, -height * 3],
    ]);

    const inner = rectPath([
      [width, height],
      [0, height],
      [width, 0],
      [0, -height],
      [-width, 0],
    ]);

    this.getLayers(doc)
      .designOnly
      .add(doc.path(outer + ' ' + inner).fill('white'));
  }

  private async loadMissingFonts(doc: Svg): Promise<void>
  {
    await Promise.all(StageUtils.svgSelectAsArray(doc, 'tspan')
      .map(t => (t as Tspan).css('font-family'))
      .filter(f => !!f)
      .map(f => this.fontInjectorService.addFont(f)));
  }

  private getOverlayWrapperSize(info: PartInfo): OverlayWrapperSize
  {
    if (this.config.rotation === 0) {
      return {
        width: info.doc.viewbox().width,
        height: info.doc.viewbox().height
      };
    }

    return {
      width: info.doc.viewbox().height,
      height: info.doc.viewbox().width
    };
  }

  private drawOverlayInlineSVGs(
      info: PartInfo, inlineSVGs: ReadonlyArray<OverlaysLayerEntryConfig>):
      Promise<Array<SvgElement>>
  {
    const wrapperSize = this.getOverlayWrapperSize(info);

    const drawSVG = async (entry_config: OverlaysLayerEntryConfig) => {
      const structure =
          await this.stageService.getInlineSVGOverlay(entry_config.location);
      const parser = new DOMParser();
      const doc = parser.parseFromString(structure, 'image/svg+xml');
      const svgNode = doc.documentElement as unknown as SVGSVGElement;
      const inlineSVGElement: Svg = SVG(svgNode);

      const wrapper = info.doc
        .nested()
        .width(wrapperSize.width)
        .height(wrapperSize.height)
        .x(0)
        .y(0)
        .css({'pointer-events': 'none'})
        .viewbox(
          0,0,
          inlineSVGElement.width() as number,
          inlineSVGElement.height() as number
        );
      wrapper.node.appendChild(inlineSVGElement.node);

      return wrapper;
    };

    return Promise.all(inlineSVGs.map(drawSVG));
  }

  private drawOverlayImages(info: PartInfo,
                            images: ReadonlyArray<OverlaysLayerEntryConfig>):
      Promise<Array<SvgElement>>
  {
    const wrapperSize = this.getOverlayWrapperSize(info);

    const tryLoad = (image: Image,
                     href: string,
                     retries: number = 0) => new Promise<void>(
      (resolve, reject) => {
        (<any>image).loaded = () => resolve();
        image.off('error');
        image.on('error', async () => {
          if (retries < StageComponent.MAX_OVERLAY_LOADING_ATTEMPTS) {
            try {
              await tryLoad(image, href, retries + 1);
            }
            catch (e) {
              reject(e);
            }
          }
          else {
            reject();
          }
        });
        image.load(href);
      });

    const drawImage = async (entry_config: OverlaysLayerEntryConfig) => {
      const wrapper = info.doc
        .nested()
        .width(wrapperSize.width)
        .height(wrapperSize.height)
        .x(0)
        .y(0)
        .attr({'pointer-events': 'none'});

      const image = injectMultiversionImageBehavior(wrapper.image())
        .width(wrapperSize.width)
        .height(wrapperSize.height)
        .x(0)
        .y(0);
      image.attr('preserveAspectRatio', 'none');

      try {
        await tryLoad(image, entry_config.location);
      } catch (e) {
        this.errorService.add(
          new ImageLoadingError(
              'image could not be loaded', [ entry_config.location ]),
          'StageComponent');
        this.errorService.flush('StageComponent');
      }

      return wrapper;
    };

    return Promise.all(images.map(drawImage));
  }

  public get showQualityRatingCheckbox(): boolean
  {
    return this.lowestImageQualityRating < QualityRating.Q4 &&
           this.imagePlaceholders.length > 1;
  }

  public updateQualityRating(): void
  {
    const placeholders = this.imagePlaceholders
      .filter((placeholder) =>
          placeholder.type === PlaceholderType.Image &&
          placeholder.isContentChangeable()
      );

    for (let placeholder of placeholders) {
      if (this.displayQualityIndicators === true) {
        placeholder.showQualityIndication();
      }
      else {
        placeholder.hideQualityIndication();
      }
    }

    const qualityRatings = placeholders
      .map((placeholder) => placeholder.getQualityRating())
      .filter((qr) => !isNaN(qr));

    this.lowestImageQualityRating = qualityRatings.length === 0 ?
        undefined : Math.min.apply(Math, qualityRatings);

    if (!this.showQualityRatingCheckbox) {
      this.displayQualityIndicators = false;
    }
  }

  public trackQualityRatingDisplay(): void
  {
    if (this.displayQualityIndicators === true) {
      const lowestQualityRatingString =
          QualityRating.getQualityString(this.lowestImageQualityRating);

      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'image_quality_check',
        label: lowestQualityRatingString,
      });
    }
  }

  private centerOverlays(doc: Svg): void
  {
    const box = doc.viewbox();
    const layers = this.getLayers(doc);
    layers.overlays.center(box.width / 2 + box.x, box.height / 2 + box.y);
  }

  private rotateIfNecessary(doc: Svg): void
  {
    if (this.preconditionsFailed() || this.config.rotation === 0) {
      return;
    }

    const layers = this.getLayers(doc);
    layers.overlays.rotate(this.config.rotation);
  }

  public getCurrentStageConfig(): StageConfig
  {
    if (this.stage === undefined || this.stageIsInCreation) {
      return this.config;
    }

    let svgStructure = this.getCleanSVGString();
    let config = <StageConfig>{
      ...this.config
    };
    config.svgStructure = svgStructure;
    return config;
  }

  /**
   * Den "gesäuberten" SVG-String aus der Stage ziehen. "Gesäubert" bedeutet:
   * Möglichst nahe an dem, was ursprünglich im Template aus der Konfiguration
   * stand.
   */
  private getCleanSVGString(): string
  {
    // Wir benutzen hier nicht die clone()-Methode von SVG.js, da diese
    // (richtigerweise) neue IDs vergibt -- wir brauchen aber die originalen
    // IDs.
    const clonedStage = SVG(this.stage.node.cloneNode(true) as SVGSVGElement);

    StageUtils.removeEditorSettings(clonedStage);

    return StageUtils.withoutNonBreakingSpaces(
        new XMLSerializer().serializeToString(clonedStage.node));
  }

  public async ngOnDestroy(): Promise<void>
  {
    await this.stageService.updateStageConfig(this.getCurrentStageConfig());
    this.subscriptions.unsubscribe();
    this.stageSubscriptions.unsubscribe();
    this.designStepService.templateChanged.unsubscribe(
        this.templateChangedSubscription);
    this.stageService.stageConfigChanged.unsubscribe(
        this.stageConfigChangedSubscription);
    if (this.newImageAvailableSubscription) {
      this.galleryService.newImageAvailable.unsubscribe(
          this.newImageAvailableSubscription);
    }

    this.svgContainer.nativeElement.getRootNode().removeEventListener(
        'click', this.hidePlaceholderMarkersIfNecessary);
  }

  public hidePlaceholderMarkersIfNecessary(event: Event): void
  {
    const rootElement = this.svgContainer.nativeElement.getRootNode();
    const clickableElements = [
        this.svgContainer.nativeElement,
        this.stageImageSelect.nativeElement,
        rootElement.querySelector('action-bar'),
        rootElement.querySelector('gallery-toolbox'),
        rootElement.querySelector('image-toolbox'),
        rootElement.querySelector('text-toolbox'),
        rootElement.querySelector('.cdk-overlay-container'),
    ];

    const isClickable = (el) => {
      if (el === document || el === rootElement) {
        return false;
      }

      if (el === undefined || clickableElements.indexOf(el) !== -1) {
        return true;
      }
      else {
        return isClickable(el.parentNode);
      }
    };

    if (isClickable(event.target)) {
      return;
    }

    if (this.focusedPlaceholder) {
      this.focusedPlaceholder.blur(true);
    }

    this.toolboxService.resetPlaceholder();
    this.galleryService.setSelectedImageId(undefined);
    for (let placeholder of this.imagePlaceholders) {
      placeholder.hidePlaceholderHighlighter();
    }
    this.placeholderService.enableTextPlaceholders();
  }
}

export function selectScoredPlaceholder(
    placeholders: Array<Image>, image: GalleryImage):
      string | undefined
{
  const aspect = galleryImageToAspect(image);

  const scored = placeholders
    .map(el => ({
      id: el.attr('id'),
      aspect: placeholderToAspect(el)
    }))
    .map(el => Object.assign(el, { distance: Math.abs(el.aspect - aspect) }))
    .sort((a, b) => a.distance - b.distance);

  return scored.length > 0 ? scored[0].id : undefined;
}

function placeholderToAspect(img: Image): number
{
  const wrapper = (<any>img).parent() as Svg;

  return dimensionsToAspect(
    parseInt(wrapper.attr('width'), 10),
    parseInt(wrapper.attr('height'), 10));
}

function galleryImageToAspect(img: GalleryImage): number
{
  return dimensionsToAspect(img.width, img.height);
}

function dimensionsToAspect(width: number, height: number): number
{
  return height >= width ? 1 : 0;
}
